import React, { useState } from 'react';
import clientService from '../../../services/clientService';
import { useTheme } from '../../../contexts/ThemeContext';

const AddClientModal = ({ isOpen, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    tel: '',
    login: '',
    password: '',
    raisonSocial: '',
    matriculeFiscal: '',
    timberFiscal: false,
    fodec: false,
    assujeti: false,
    exonore: false,
    observation: '',
    annuelle: false,
    logo: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Le fichier logo ne peut pas dépasser 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setFormData(prev => ({ ...prev, logo: base64 }));
        setLogoPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
  };

  const [logoPreview, setLogoPreview] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.raisonSocial.trim()) {
        throw new Error('La raison sociale est obligatoire');
      }
      if (!formData.login.trim()) {
        throw new Error('Le login est obligatoire');
      }
      if (!formData.password.trim()) {
        throw new Error('Le mot de passe est obligatoire');
      }

      const result = await clientService.createClient(formData);
      
      if (result.success) {
        onSuccess();
        resetForm();
      } else {
        setError(result.message || 'Erreur lors de la création du client');
      }
    } catch (error) {
      console.error('Create client error:', error);
      setError(error.message || 'Erreur lors de la création du client');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      tel: '',
      login: '',
      password: '',
      raisonSocial: '',
      matriculeFiscal: '',
      timberFiscal: false,
      fodec: false,
      assujeti: false,
      exonore: false,
      observation: '',
      annuelle: false,
      logo: null
    });
    setLogoPreview(null);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 ${
          isDarkMode 
            ? 'bg-gradient-to-r from-dark-primary-600 to-dark-primary-700' 
            : 'bg-gradient-to-r from-primary-600 to-primary-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-3">
                <i className="fas fa-user-plus text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Nouveau Client</h2>
                <p className={`text-sm ${
                  isDarkMode ? 'text-dark-primary-100' : 'text-primary-100'
                }`}>
                  Créer un nouveau client
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className={`mb-6 p-4 rounded-xl border ${
                isDarkMode 
                  ? 'bg-red-900/50 border-red-800 text-red-300' 
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <div className="flex items-center">
                  <i className={`fas fa-exclamation-circle mr-3 ${
                    isDarkMode ? 'text-red-400' : 'text-red-500'
                  }`}></i>
                  <div>
                    <div className="font-medium">Erreur de validation</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg mr-3 ${
                    isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'
                  }`}>
                    <i className={`fas fa-info-circle ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Informations générales
                  </h3>
                  <span className={`ml-2 text-xs px-2 py-1 rounded ${
                    isDarkMode ? 'text-red-300 bg-red-900/50' : 'text-red-600 bg-red-50'
                  }`}>
                    * Champs obligatoires
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`fas fa-user mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      Nom
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Nom du client"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`fas fa-user mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      Prénom
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Prénom du client"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`fas fa-phone mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="tel"
                      value={formData.tel}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Ex: +216 XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`fas fa-user mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      Login *
                    </label>
                    <input
                      type="text"
                      name="login"
                      value={formData.login}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Nom d'utilisateur"
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`fas fa-key mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      Mot de passe *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Mot de passe"
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`fas fa-building mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      Raison sociale *
                    </label>
                    <input
                      type="text"
                      name="raisonSocial"
                      value={formData.raisonSocial}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Ex: Société Générale de Sécurité"
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`fas fa-file-invoice mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      Matricule fiscal
                    </label>
                    <input
                      type="text"
                      name="matriculeFiscal"
                      value={formData.matriculeFiscal}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Matricule fiscal du client"
                    />
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}> <i className={`fas fa-cogs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}></i> </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}> Options et statuts </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className={`flex items-center p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}> <input type="checkbox" id="timberFiscal" name="timberFiscal" checked={formData.timberFiscal} onChange={handleInputChange} className={`h-4 w-4 rounded focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'text-dark-primary-600 focus:ring-dark-primary-500 bg-gray-700 border-gray-600' : 'text-primary-600 focus:ring-primary-500 border-gray-300'}`} /> <label htmlFor="timberFiscal" className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}> <i className={`fas fa-stamp mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i> Timber fiscal </label> </div>
                  <div className={`flex items-center p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}> <input type="checkbox" id="fodec" name="fodec" checked={formData.fodec} onChange={handleInputChange} className={`h-4 w-4 rounded focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'text-dark-primary-600 focus:ring-dark-primary-500 bg-gray-700 border-gray-600' : 'text-primary-600 focus:ring-primary-500 border-gray-300'}`} /> <label htmlFor="fodec" className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}> <i className={`fas fa-percent mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i> FODEC </label> </div>
                  <div className={`flex items-center p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}> <input type="checkbox" id="assujeti" name="assujeti" checked={formData.assujeti} onChange={handleInputChange} className={`h-4 w-4 rounded focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'text-dark-primary-600 focus:ring-dark-primary-500 bg-gray-700 border-gray-600' : 'text-primary-600 focus:ring-primary-500 border-gray-300'}`} /> <label htmlFor="assujeti" className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}> <i className={`fas fa-check mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i> Assujetti </label> </div>
                  <div className={`flex items-center p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}> <input type="checkbox" id="exonore" name="exonore" checked={formData.exonore} onChange={handleInputChange} className={`h-4 w-4 rounded focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'text-dark-primary-600 focus:ring-dark-primary-500 bg-gray-700 border-gray-600' : 'text-primary-600 focus:ring-primary-500 border-gray-300'}`} /> <label htmlFor="exonore" className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}> <i className={`fas fa-minus-circle mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i> Exonéré </label> </div>
                </div>
              </div>

              {/* Déclaration périodicité switcher */}
              <div className={`flex items-start p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mr-3 mt-1">
                  <i className="fas fa-calendar-alt text-purple-600"></i>
                </div>
                <div className="flex-1">
                  <label className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Entretien</label>
                  <p className="text-sm text-gray-500">Choisissez la périodicité</p>
                  <div className="mt-3 flex bg-gray-200 rounded-full p-1 w-fit">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, annuelle: true }))}
                      className={`px-4 py-1 rounded-full font-medium transition-all duration-200
                        ${formData.annuelle
                          ? 'bg-primary-600 text-white shadow'
                          : 'text-gray-700 hover:bg-gray-300'}
                      `}
                      style={{ minWidth: 110 }}
                    >
                      Annuelle
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, annuelle: false }))}
                      className={`px-4 py-1 rounded-full font-medium transition-all duration-200
                        ${!formData.annuelle
                          ? 'bg-primary-600 text-white shadow'
                          : 'text-gray-700 hover:bg-gray-300'}
                      `}
                      style={{ minWidth: 110 }}
                    >
                      Semestrielle
                    </button>
                  </div>
                </div>
              </div>

              {/* Observations */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}> <i className={`fas fa-sticky-note ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}></i> </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}> Observations </h3>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}> <i className={`fas fa-comment mr-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i> Notes et remarques </label>
                  <textarea name="observation" value={formData.observation} onChange={handleInputChange} rows={4} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors resize-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`} placeholder="Ajoutez des notes ou observations concernant ce client..."></textarea>
                </div>
              </div>

              {/* Logo Upload */}
              <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-200'}`}>
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-indigo-900/50' : 'bg-indigo-100'}`}> <i className={`fas fa-image ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}></i> </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}> Logo du client </h3>
                </div>
                <div className="flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-6">
                  {logoPreview && (
                    <div className="flex-shrink-0 relative">
                      <img src={logoPreview} alt="Preview" className="w-24 h-24 object-contain rounded-2xl border-2 border-gray-200 bg-white shadow-sm" />
                      <button type="button" onClick={removeLogo} className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600 transition-all duration-200 shadow-lg hover:scale-110"> × </button>
                    </div>
                  )}
                  <div className="flex-1">
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" id="logo-upload-add" />
                    <label htmlFor="logo-upload-add" className="block w-full p-6 border-2 border-dashed border-indigo-300 rounded-2xl text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 shadow-sm"> <i className="fas fa-cloud-upload-alt text-3xl text-indigo-400 mb-3"></i> <p className="text-base font-medium text-indigo-700 mb-1">{logoPreview ? 'Changer le logo' : 'Ajouter un logo'}</p> <p className="text-sm text-indigo-500">PNG, JPG jusqu'à 5MB</p> </label>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {formData.raisonSocial && (
                <div className={`rounded-xl p-6 border ${
                  isDarkMode ? 'bg-dark-primary-900/20 border-dark-primary-800' : 'bg-primary-50 border-primary-200'
                }`}>
                  <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg mr-3 ${
                      isDarkMode ? 'bg-dark-primary-900/50' : 'bg-primary-100'
                    }`}>
                      <i className={`fas fa-eye ${
                        isDarkMode ? 'text-dark-primary-400' : 'text-primary-600'
                      }`}></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Aperçu du client
                    </h3>
                  </div>
                  
                  <div className={`rounded-lg p-4 border ${
                    isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-primary-200'
                  }`}>
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                        isDarkMode ? 'bg-dark-primary-900/50 text-dark-primary-400' : 'bg-primary-100 text-primary-600'
                      }`}>
                        <i className="fas fa-building text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formData.raisonSocial}
                        </h4>
                        <div className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {formData.login && (
                            <div>
                              <i className="fas fa-user mr-1 w-4"></i>
                              {formData.login}
                            </div>
                          )}
                          {formData.tel && (
                            <div>
                              <i className="fas fa-phone mr-1 w-4"></i>
                              {formData.tel}
                            </div>
                          )}
                          {formData.matriculeFiscal && (
                            <div>
                              <i className="fas fa-file-invoice mr-1 w-4"></i>
                              {formData.matriculeFiscal}
                            </div>
                          )}
                        </div>
                        {/* Options badges */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {formData.timberFiscal && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                            }`}>
                              Timber
                            </span>
                          )}
                          {formData.assujeti && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'
                            }`}>
                              Assujetti
                            </span>
                          )}
                          {formData.exonore && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800'
                            }`}>
                              Exonéré
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${
          isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 border rounded-lg transition-colors font-medium ${
                isDarkMode 
                  ? 'text-gray-300 bg-gray-800 border-gray-600 hover:bg-gray-700' 
                  : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !formData.raisonSocial.trim()}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                loading || !formData.raisonSocial.trim()
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                  ? 'bg-dark-primary-600 hover:bg-dark-primary-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Création...
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  Créer le client
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;